import { AutoCustomCompleteField, CustomDateControl, CustomTimefield, FormField, FormGrid, FormShell, Input, MessageAlert, Radio, RBButton } from '@scmlatam/web-ui';
import type { UpdatePaycodeDialogProps, AlertProps, FooterProps } from '../types/component';

const Alert = ({ t, validationMessage, setValidationMessage, showMessage, setShowMessage, onClickCancel }: AlertProps) => {
    if (validationMessage) {
        return (
            <div id="schedule-update-paycode-alert-validation">
                <MessageAlert
                    type="warning"
                    title={validationMessage}
                    message=""
                    className="rb-message-alert--form"
                    actions={[
                        {
                            label: 'OK',
                            onClick: () => setValidationMessage(''),
                        },
                    ]}
                />
            </div>
        );
    }

    if (showMessage) {
        return (
            <div id="schedule-update-paycode-alert-unsaved">
                <MessageAlert
                    type="warning"
                    title={t('edit_paycode.alerts.unsaved_changes.title')}
                    message={t('edit_paycode.alerts.unsaved_changes.message')}
                    className="rb-message-alert--form"
                    actions={[
                        {
                            label: t('edit_paycode.alerts.unsaved_changes.action_button'),
                            onClick: () => setShowMessage(false),
                        },
                        {
                            label: t('edit_paycode.alerts.unsaved_changes.cancel_button'),
                            onClick: () => {
                                setShowMessage(false);
                                onClickCancel();
                            },
                        },
                    ]}
                />
            </div>
        );
    }

    return undefined;
};

const Footer = ({ t, loading, onClickCancel, onClickSave }: FooterProps) => (
    <div id="schedule-update-paycode-footer" className="flex flex-wrap items-center justify-end gap-3 p-3">
        <RBButton id="schedule-update-paycode-cancel" variant="secondary" size="m" disabled={loading} onClick={onClickCancel}>
            {t('edit_paycode.buttons.cancel')}
        </RBButton>
        <RBButton id="schedule-update-paycode-save" size="m" disabled={loading} onClick={onClickSave}>
            {t('edit_paycode.buttons.save')}
        </RBButton>
    </div>
);

const UpdatePaycodeDialog = ({ formProps }: UpdatePaycodeDialogProps) => {
    const { formData, setFormData, t, handleCancel } = formProps;

    return (
        <FormShell
            open={formProps.openForm}
            title={formProps.title}
            size="m"
            onClose={handleCancel}
            loading={formProps.loading || formProps.loadingAssign}
            alert={
                <Alert
                    t={t}
                    validationMessage={formProps.validationMessage}
                    setValidationMessage={formProps.setValidationMessage}
                    showMessage={formProps.showMessage}
                    setShowMessage={formProps.setShowMessage}
                    onClickCancel={formProps.closeForm}
                />
            }
            footer={<Footer t={t} loading={formProps.loading} onClickCancel={handleCancel} onClickSave={formProps.submit} />}
        >
            <FormGrid id="schedule-update-paycode-form">
                <FormField span={12}>
                    <AutoCustomCompleteField
                        id="schedule-update-paycode-worker"
                        label={t('edit_paycode.fields.worker')}
                        placeholder={t('edit_paycode.placeholder.select')}
                        values={formProps.employees}
                        value={formData.employeeId}
                        setValue={(employeeId: any) => setFormData({ ...formData, employeeId })}
                        disabled={true}
                        selectionMode="single"
                        white
                        error={formProps.validationErrors.employeeId.error}
                        helperText={formProps.validationErrors.employeeId.error ? formProps.validationErrors.employeeId.message : ''}
                        search={{
                            value: formProps.searchEmployee,
                            onChange: formProps.setSearchEmployee,
                            placeholder: t('edit_paycode.placeholder.select'),
                            mode: 'local',
                            emptyText: t('edit_paycode.placeholder.select'),
                        }}
                    />
                </FormField>

                <FormField span={12}>
                    <AutoCustomCompleteField
                        id="schedule-update-paycode-paycode"
                        label={t('edit_paycode.fields.paycode')}
                        placeholder={t('edit_paycode.placeholder.select')}
                        values={formProps.paycodes}
                        value={formData.paycode}
                        setValue={(paycode: any) => setFormData({ ...formData, paycode })}
                        disabled={formProps.loadingPaycodes || formProps.loadingAssign}
                        selectionMode="single"
                        white
                        error={formProps.validationErrors.paycode.error}
                        helperText={formProps.validationErrors.paycode.error ? formProps.validationErrors.paycode.message : ''}
                        search={{
                            value: formProps.searchPaycode,
                            onChange: formProps.setSearchPaycode,
                            placeholder: t('edit_paycode.placeholder.select'),
                            mode: 'local',
                            emptyText: t('edit_paycode.placeholder.select'),
                        }}
                    />
                </FormField>

                <FormField span={12}>
                    <div id="schedule-update-paycode-mode" className="flex h-full items-center gap-3">
                        <span className="text-md font-medium">{t('edit_paycode.fields.type_amount')}</span>
                        <Radio
                            id="schedule-update-paycode-mode-relative"
                            name="paycode-mode-update"
                            value="relative_amount"
                            checked={formData.mode === 'relative_amount'}
                            onChange={() => setFormData({ ...formData, mode: 'relative_amount' })}
                        >
                            {t('edit_paycode.fields.relative_amount')}
                        </Radio>
                        <Radio
                            id="schedule-update-paycode-mode-numerical"
                            name="paycode-mode-update"
                            value="numerical_amount"
                            checked={formData.mode === 'numerical_amount'}
                            onChange={() => setFormData({ ...formData, mode: 'numerical_amount' })}
                        >
                            {t('edit_paycode.fields.numerical_amount')}
                        </Radio>
                    </div>
                </FormField>

                <FormField id="schedule-update-paycode-date" span={6}>
                    <CustomDateControl
                        label={t('edit_paycode.fields.date')}
                        initialValue={null}
                        value={formData.date}
                        setValue={(date) => setFormData({ ...formData, date })}
                        disabled={false}
                        white
                        fullWidth
                        error={formProps.validationErrors.date.error}
                        helperText={formProps.validationErrors.date.error ? formProps.validationErrors.date.message : ''}
                    />
                </FormField>

                <FormField id="schedule-update-paycode-start" span={6}>
                    <CustomTimefield
                        label={t('edit_paycode.fields.start_at')}
                        initialValue={null}
                        value={formData.start}
                        setValue={(start) => setFormData({ ...formData, start })}
                        disabled={false}
                        white
                        fullWidth
                        error={formProps.validationErrors.start.error}
                        helperText={formProps.validationErrors.start.error ? formProps.validationErrors.start.message : ''}
                    />
                </FormField>

                <FormField span={6}>
                    <Input
                        id="schedule-update-paycode-amount-days"
                        type="number"
                        label={t('edit_paycode.fields.amount_of_days')}
                        labelVariant="notched"
                        value={formData.amountDays}
                        onChange={(event) => setFormData({ ...formData, amountDays: event.target.value })}
                        min={1}
                        error={formProps.validationErrors.amountDays.error}
                        helperText={formProps.validationErrors.amountDays.error ? formProps.validationErrors.amountDays.message : ''}
                    />
                </FormField>

                <FormField id={formData.mode === 'numerical_amount' ? 'schedule-update-paycode-amount-hours-numerical' : undefined} span={6}>
                    {formData.mode === 'numerical_amount' ? (
                        <CustomTimefield
                            label={t('edit_paycode.fields.amount_of_hours')}
                            initialValue={null}
                            value={formData.amountHours}
                            setValue={(amountHours) => setFormData({ ...formData, amountHours })}
                            disabled={false}
                            white
                            fullWidth
                            error={formProps.validationErrors.amountHours.error}
                            helperText={formProps.validationErrors.amountHours.error ? formProps.validationErrors.amountHours.message : ''}
                        />
                    ) : (
                        <AutoCustomCompleteField
                            id="schedule-update-paycode-amount-hours-relative"
                            label={t('edit_paycode.fields.amount_of_hours')}
                            placeholder={t('edit_paycode.placeholder.select')}
                            values={[
                                { label: t('edit_paycode.fields.planned_full_day'), value: '1' },
                                { label: t('edit_paycode.fields.planned_half_day'), value: '2' },
                            ]}
                            value={String(formData.amountRelativeHours)}
                            setValue={(value) =>
                                setFormData({
                                    ...formData,
                                    amountRelativeHours: (Number(value) === 2 ? 2 : 1) as 1 | 2,
                                })
                            }
                            selectionMode="single"
                            white
                        />
                    )}
                </FormField>

                <FormField span={12}>
                    <Input
                        id="schedule-update-paycode-note"
                        as="textarea"
                        label={t('edit_paycode.fields.note')}
                        labelVariant="notched"
                        placeholder={t('edit_paycode.placeholder.note')}
                        value={formData.note}
                        onChange={(event) => setFormData({ ...formData, note: event.target.value })}
                    />
                </FormField>

                <FormField span={12}>
                    <Input
                        id="schedule-update-paycode-comment"
                        as="textarea"
                        label={t('edit_paycode.fields.comment')}
                        labelVariant="notched"
                        placeholder={t('edit_paycode.placeholder.comment')}
                        value={formData.comment}
                        onChange={(event) => setFormData({ ...formData, comment: event.target.value })}
                    />
                </FormField>
            </FormGrid>
        </FormShell>
    );
};

export { UpdatePaycodeDialog };
